<template>
  <div>
    <h1 class="title">Client’s analytics</h1>
    <app-cells>
      <app-form-group label="Tour dates">
        <div class="date-picker-wrapper">
          <VueCtkDateTimePicker
            id="date_picker_balance"
            class="custom-date-time-picker custom-date-time-picker--range"
            v-model="filter_dates"
            :custom-shortcuts="custom_shortcuts"
            shortcut="thisYear"
            @is-hidden="onIsHiddenPicker"
            format="YYYY-MM-DD"
            formatted="ll"
            label="Select dates"
            color="#77b017"
            button-color="#77b017"
            overlay
            no-header
            no-label
            locale="en"
            range
            noClearButton
          />
          <div v-if="date_picker_error" class="date-picker-wrapper__error">Choose an end date</div>
        </div>
      </app-form-group>
      <app-form-group label="Confirmation date limit">
        <VueCtkDateTimePicker
          id="date_picker_date_limit"
          class="custom-date-time-picker"
          v-model="confirmation_date_limit"
          @is-hidden="onIsHiddenPicker"
          @input="onInputPicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Select date"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          only-date
          ref="date_picker"
        />
      </app-form-group>
    </app-cells>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells><h2 class="title">Tour start</h2></app-cells>
      <div class="info-row">
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ tour_start_data.clients.toLocaleString() }}</span>
            <span class="info-row__text">Clients</span>
          </div>
        </div>
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ tour_start_data.groups.toLocaleString() }}</span>
            <span class="info-row__text">Groups</span>
          </div>
        </div>
      </div>
      <div class="charts-grid">
        <highcharts :options="graph_options" />
      </div>
      <app-cells><h2 class="title">Estimated income</h2></app-cells>
      <div class="info-row">
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ estimated_data.amount.toLocaleString() }}$</span>
            <span class="info-row__text">Total</span>
          </div>
        </div>
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ estimated_data.commission.toLocaleString() }}$</span>
            <span class="info-row__text">Commission</span>
          </div>
        </div>
      </div>
      <div class="charts-grid">
        <highcharts :options="graph_options_estimated" />
      </div>
      <app-cells><h2 class="title">Climbing</h2></app-cells>
      <div class="info-row">
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ climbing_data.total_clients.toLocaleString() }}</span>
            <span class="info-row__text">Clients</span>
          </div>
        </div>
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ climbing_data.total_groups.toLocaleString() }}</span>
            <span class="info-row__text">Climbing groups</span>
          </div>
        </div>
      </div>
      <div class="charts-grid">
        <highcharts :options="graph_options_climbing" />
      </div>
      <app-cells><h2 class="title">Safari</h2></app-cells>
      <div class="info-row">
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ safari_data.total_clients.toLocaleString() }}</span>
            <span class="info-row__text">Clients</span>
          </div>
        </div>
        <div class="info-row__item">
          <div class="info-row__content">
            <span class="info-row__value">{{ safari_data.total_groups.toLocaleString() }}</span>
            <span class="info-row__text">Safari groups</span>
          </div>
        </div>
      </div>
      <div class="charts-grid">
        <highcharts :options="graph_options_safari" />
      </div>
    </template>
  </div>
</template>

<script>
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import moment from 'moment/moment'
import {
  getClientsAnalyticsEstimatedIncome,
  getClientsAnalyticsTourStart,
  getClimbingGroupsClientsAnalytics,
  getSafariAnalyticsClients,
} from '@/http'
import { Chart } from 'highcharts-vue'

export default {
  name: 'ClientsAnalytics',
  components: { highcharts: Chart, VueCtkDateTimePicker },
  data() {
    return {
      is_loaded: false,
      filter_dates: null,
      date_picker_error: false,
      confirmation_date_limit: null,
      custom_shortcuts: [
        {
          key: 'next30Days',
          label: 'Next 30 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(30, 'days')
            }
          }
        },
        {
          key: 'next60Days',
          label: 'Next 60 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(60, 'days')
            }
          }
        },
        {
          key: 'next90Days',
          label: 'Next 90 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(90, 'days')
            }
          }
        },
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        { key: 'thisYear', label: 'This year', value: 'year' },
        {
          key: 'nextYear',
          label: 'Next year',
          value: () => {
            const nextYear = moment().add(1, 'year').year()
            return {
              start: moment().year(nextYear).startOf('year'),
              end: moment().year(nextYear).endOf('year')
            }
          }
        }
      ],
      graph_options: {
        accessibility: {
          enabled: false,
        },
        CSSObject: {
          fontFamily: 'regular',
          fontSize: '6.2em',
        },
        chart: {
          type: 'spline'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: [],
        },
        yAxis: {
          title: {
            text: ''
          }
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
            }
          }
        },
        series: []
      },
      graph_options_estimated: {
        accessibility: {
          enabled: false,
        },
        CSSObject: {
          fontFamily: 'regular',
          fontSize: '6.2em',
        },
        chart: {
          type: 'spline'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: [],
        },
        yAxis: {
          title: {
            text: ''
          }
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
            }
          }
        },
        series: []
      },
      graph_options_climbing: {
        accessibility: {
          enabled: false,
        },
        CSSObject: {
          fontFamily: 'regular',
          fontSize: '6.2em',
        },
        chart: {
          type: 'spline'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: [],
        },
        yAxis: {
          title: {
            text: ''
          }
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
            }
          }
        },
        series: []
      },
      graph_options_safari: {
        accessibility: {
          enabled: false,
        },
        CSSObject: {
          fontFamily: 'regular',
          fontSize: '6.2em',
        },
        chart: {
          type: 'spline'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: [],
        },
        yAxis: {
          title: {
            text: ''
          }
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
            }
          }
        },
        series: []
      },
      tour_start_data: {
        clients: 0,
        groups: 0,
      },
      estimated_data: {
        amount: 0,
        commission: 0,
      },
      climbing_data: {
        total_clients: 0,
        total_groups: 0,
      },
      safari_data: {
        total_clients: 0,
        total_groups: 0,
      },
    }
  },
  created() {
    setTimeout(() => {
      this.$nextTick(() => {
        this.fetchGraphs()
      })
    }, 0)
  },
  methods: {
    fetchGraphs() {
      this.fetchGraph()
      this.fetchGraphEstimated()
      this.fetchGraphClimbing()
      this.fetchGraphSafari()
      this.is_loaded = false
    },
    onIsHiddenPicker() {
      if (this.filter_dates?.start && this.filter_dates?.end) {
        this.date_picker_error = false
        this.fetchGraphs()
      } else this.date_picker_error = true
    },
    onInputPicker() {
      if (!this.$refs.date_picker.pickerOpen) this.fetchGraphs()
    },
    fetchGraph() {
      getClientsAnalyticsTourStart({from_date: this.filter_dates.start, to_date: this.filter_dates.end, to_confirmation_date: this.confirmation_date_limit})
        .then(response => {
          this.graph_options.xAxis.categories = response.data.graph.categories
          this.graph_options.series = response.data.graph.series
          this.tour_start_data.clients = response.data.total_clients
          this.tour_start_data.groups = response.data.total_groups
        })
    },
    fetchGraphEstimated() {
      getClientsAnalyticsEstimatedIncome({
        from_date: this.filter_dates.start,
        to_date: this.filter_dates.end,
        to_confirmation_date: this.confirmation_date_limit
      })
        .then(response => {
          this.graph_options_estimated.xAxis.categories = response.data.graph.categories
          this.graph_options_estimated.series = response.data.graph.series
          this.estimated_data.amount = response.data.total_amount
          this.estimated_data.commission = response.data.total_commission
          this.is_loaded = true
        })
    },
    fetchGraphClimbing() {
      getClimbingGroupsClientsAnalytics(
        this.filter_dates.start,
        this.filter_dates.end,
        this.confirmation_date_limit
      )
        .then(response => {
          this.graph_options_climbing.xAxis.categories = response.data.graph.categories
          this.graph_options_climbing.series = response.data.graph.series
          this.climbing_data.total_clients = response.data.total_clients
          this.climbing_data.total_groups = response.data.total_groups
          this.is_loaded = true
        })
    },
    fetchGraphSafari() {
      getSafariAnalyticsClients(
        this.filter_dates.start,
        this.filter_dates.end,
        this.confirmation_date_limit
      )
        .then(response => {
          this.graph_options_safari.xAxis.categories = response.data.graph.categories
          this.graph_options_safari.series = response.data.graph.series
          this.safari_data.total_clients = response.data.total_clients
          this.safari_data.total_groups = response.data.total_groups
          this.is_loaded = true
        })
    },
  }
}
</script>
<style lang="sass">
.date-picker-wrapper
  position: relative

.date-picker-wrapper__error
  position: absolute
  bottom: -18px
  color: $color-error
  font-size: 12px
</style>
